import {
  clearSupabaseAuthStorage,
  isInvalidRefreshTokenError,
  isSupabaseConfigured,
  supabase,
} from './supabaseClient.js';

const SESSION_KEY = 'n3bula-session-v1';
export const APP_SESSION_DURATION_MS = 12 * 60 * 60 * 1000;
export const APP_SESSION_DURATION_HOURS = 12;

function normalizeAppSession(session = {}) {
  const issuedAt = Number(session.issuedAt) || Date.now();
  const expiresAt = Number(session.expiresAt) || issuedAt + APP_SESSION_DURATION_MS;
  return {
    ...session,
    issuedAt,
    expiresAt,
    sessionDurationHours: APP_SESSION_DURATION_HOURS,
  };
}

function isAppSessionExpired(session = {}) {
  return Boolean(session.expiresAt && Number(session.expiresAt) <= Date.now());
}

export function getSession() {
  const raw = localStorage.getItem(SESSION_KEY);
  if (!raw) return null;
  try {
    const parsedRaw = JSON.parse(raw);
    const parsed = normalizeAppSession(parsedRaw);
    if (isAppSessionExpired(parsed)) {
      clearSession();
      return null;
    }
    if (!parsedRaw.issuedAt || !parsedRaw.expiresAt || parsedRaw.sessionDurationHours !== APP_SESSION_DURATION_HOURS) {
      setSession(parsed);
    }
    return parsed;
  } catch {
    clearSession();
    return null;
  }
}

export function setSession(session) {
  localStorage.setItem(SESSION_KEY, JSON.stringify(normalizeAppSession(session)));
}

export function clearSession() {
  localStorage.removeItem(SESSION_KEY);
}

function clearAuthState() {
  clearSession();
  clearSupabaseAuthStorage();
}

function sessionFromUser(user) {
  const email = user?.email || '';
  const metaName = user?.user_metadata?.name || user?.user_metadata?.full_name || '';
  const fallbackName = email.split('@')[0]?.replace(/[._-]/g, ' ') || 'N3bula User';
  const name = metaName || fallbackName.replace(/\b\w/g, (char) => char.toUpperCase());
  return { email, name, id: user?.id || null, sessionDurationHours: APP_SESSION_DURATION_HOURS };
}

export async function validateActiveSession() {
  const appSession = getSession();
  if (!appSession) {
    clearAuthState();
    return null;
  }

  if (!isSupabaseConfigured || !supabase) return appSession;

  try {
    const { data, error } = await supabase.auth.getSession();
    if (error || !data?.session) {
      clearAuthState();
      return null;
    }

    const nextSession = {
      ...appSession,
      ...sessionFromUser(data.session.user),
      issuedAt: appSession.issuedAt,
      expiresAt: appSession.expiresAt,
    };
    setSession(nextSession);
    return nextSession;
  } catch (error) {
    if (isInvalidRefreshTokenError(error)) clearAuthState();
    return null;
  }
}
